/**
 * CaseStudyBananaBoat.jsx
 *
 * Anchor-customer case study for Banana Boat Rentals (Austin, TX) — Austin's
 * largest party-boat fleet, running their booking platform AND custom website
 * on BookingFlows.
 *
 * Design intent:
 *   - Same one-pager sales-weapon format as CaseStudyBOATX, so the team can
 *     drop it into an email after a discovery call.
 *   - Lives at /customers/banana-boat-rentals.
 *   - BBR brand flavor: black + yellow hero accents (vs BOATX's blue) so the
 *     two case studies read as a family but each nods to the customer's brand.
 *   - No GMV figure by request — leads with fleet size, reviews, and the
 *     clean modern site + booking infra we built for them.
 */

import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ArrowRight, Star, Users, MapPin, Calendar, Sparkles,
  CheckCircle2, Quote, ChevronRight, Globe, Phone, Mail, Anchor,
  CreditCard, Smartphone, BarChart3, Shield, Zap,
} from "lucide-react";
import { proxyImg } from "../utils/img";

const BBR_SITE = "https://www.bananaboatrentals.com/";

/** Google "G" mark (official 4-color), inlined to avoid a heavy icon dep. */
const GoogleGIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// ---------------------------------------------------------------------------
// Page data
// ---------------------------------------------------------------------------

const BBR_LOGO = "https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_logo.png";

// Sharp lifestyle photos from real Banana Boat Rentals charters on Lake Austin.
const HERO_PHOTOS = [
  "https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_hero_bridge.jpg",
  "https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_party_group.jpg",
  "https://1iynnenkjdavmviy.public.blob.vercel-storage.com/bbr_yellow_pontoon.jpg",
];

// Screenshots of the clean, modern site we built + booking infra running on it.
const SITE_SHOTS = [
  {
    src: "https://customer-assets.emergentagent.com/job_d269599a-400d-440c-92a3-9218d3ccd3af/artifacts/1tapvxni_Screenshot%202026-06-12%20at%209.40.35%E2%80%AFPM.png",
    title: "Homepage hero — live on bananaboatrentals.com",
    caption: "Bold, mobile-first hero leading with social proof (500+ Google reviews, 5.0 stars), the 15+ boat fleet, and a one-tap \u201CBook Now\u201D that drops straight into the BookingFlows widget.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_d269599a-400d-440c-92a3-9218d3ccd3af/artifacts/35n093c8_Screenshot%202026-06-12%20at%209.41.10%E2%80%AFPM.png",
    title: "The fleet — premium boats for every occasion",
    caption: "All 15+ party boats and pontoons in one place with live availability, capacity, and pricing. Every card books in under 60 seconds, captain included.",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_d269599a-400d-440c-92a3-9218d3ccd3af/artifacts/c7zsf4w6_Screenshot%202026-06-12%20at%2010.32.39%E2%80%AFPM.png",
    title: "Social proof — \u201CSee the vibes in action\u201D",
    caption: "An embedded Instagram reel section turns real customer moments into conversion fuel, right above a sticky Book Now CTA.",
  },
];

const HEADLINE_STATS = [
  { label: "Fleet size",     value: "15+",   sub: "Austin's largest",     icon: Anchor,  color: "from-yellow-300 to-amber-500" },
  { label: "Google reviews", value: "500+",  sub: "5.0 average",          icon: "google", color: "from-amber-400 to-orange-500" },
  { label: "Lakes served",   value: "2",     sub: "Lake Austin & Travis", icon: MapPin,  color: "from-emerald-400 to-teal-600" },
  { label: "Online booking", value: "<60s",  sub: "captain included",     icon: Calendar, color: "from-blue-400 to-indigo-600" },
];

const FEATURES_USED = [
  { icon: Globe,       title: "Custom website + local SEO", body: "We designed and built bananaboatrentals.com from the ground up — a clean, modern, mobile-first site engineered to rank for 'Austin boat rental', 'Lake Travis party boat', and 'Lake Austin pontoon rental'. Fast loads, schema markup, content tuned for the searches that convert." },
  { icon: Calendar,    title: "Real-time booking widget",  body: "Guests reserve any of 15+ boats in under 60 seconds with live availability and captain included — no phone tag, no email back-and-forth. Embedded right on their own domain." },
  { icon: Anchor,      title: "Fleet & asset management",  body: "All 15+ party boats, pontoons, and barges live in one place — per-asset availability, pricing, and maintenance blocks. The largest fleet in Austin, organized." },
  { icon: Users,       title: "Captain dispatch",          body: "Assignment-on-Confirmation routes every charter to a licensed captain automatically. Everyone knows which boat they're running by Friday morning." },
  { icon: CreditCard,  title: "Stripe Connect payments",   body: "Deposits collected at booking, balances on trip day, instant payouts to the bank. Refunds and gratuity stay clean." },
  { icon: BarChart3,   title: "GA4 + Google Ads tracking", body: "Multi-tenant conversion tracking pipes every booking back to their own Google Ads account so they can scale ad spend with confidence." },
  { icon: Smartphone,  title: "Mobile-first checkout",     body: "Built iPhone-out — because nearly every Austin lake booking happens on a phone, often the morning of the trip." },
];

const RESULTS = [
  { label: "Fleet online",      value: "15+ boats", sub: "All bookable in real time." },
  { label: "Local SEO",         value: "Top results", sub: "For Austin lake rental searches." },
  { label: "Booking time",      value: "<60 sec",   sub: "Captain included, no phone tag." },
  { label: "Brand experience",  value: "Seamless",  sub: "Their site, their brand, our engine." },
];

const SERVING = [
  "Lake Austin", "Lake Travis", "Pennybacker / 360 Bridge", "Downtown Austin",
  "Bachelor parties", "Bachelorette celebrations", "Corporate events", "Family outings",
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CaseStudyBananaBoat() {
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="case-study-banana-boat-page">
      <Helmet>
        <title>How Banana Boat Rentals runs Austin's largest party-boat fleet on BookingFlows | Case Study</title>
        <meta name="description" content="Banana Boat Rentals — Austin's largest party-boat fleet with 15+ boats and 500+ 5-star Google reviews — runs their booking platform AND custom website on BookingFlows. Custom site, local SEO, real-time booking, fleet management, and captain dispatch across Lake Austin & Lake Travis." />
        <meta property="og:title" content="How Banana Boat Rentals runs Austin's largest party-boat fleet on BookingFlows" />
        <meta property="og:description" content="Custom modern website, local SEO, real-time booking widget, fleet management, and captain dispatch — the full stack behind Austin's largest party-boat fleet." />
        <meta property="og:image" content={HERO_PHOTOS[0]} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How Banana Boat Rentals runs on BookingFlows" />
        <meta name="twitter:image" content={HERO_PHOTOS[0]} />
        <link rel="canonical" href="https://bookingflows.com/customers/banana-boat-rentals" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "How Banana Boat Rentals runs Austin's largest party-boat fleet on BookingFlows",
          "image": HERO_PHOTOS,
          "publisher": { "@type": "Organization", "name": "BookingFlows", "logo": { "@type": "ImageObject", "url": "https://bookingflows.com/logo-icon.png" } },
          "about": { "@type": "Organization", "name": "Banana Boat Rentals", "url": BBR_SITE, "areaServed": "Austin, TX", "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "500" } },
          "mainEntityOfPage": "https://bookingflows.com/customers/banana-boat-rentals"
        })}</script>
      </Helmet>

      {/* ---------- Top nav ---------- */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="bbr-nav-home">
            <img src="/logo-icon.png" alt="BookingFlows" className="w-9 h-9 rounded-lg object-cover shadow-sm" />
            <span className="text-xl font-bold tracking-tight text-gray-900 group-hover:text-blue-600 transition-colors">BookingFlows</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <a href={BBR_SITE} target="_blank" rel="noopener noreferrer"
               className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors" data-testid="bbr-nav-visit">
              Visit bananaboatrentals.com <ChevronRight className="w-3.5 h-3.5" />
            </a>
            <Link to="/signup"
               className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium transition-colors shadow-sm"
               data-testid="bbr-nav-cta">
              Book a demo
            </Link>
          </div>
        </div>
      </nav>

      {/* ---------- Hero (black + yellow, BBR brand) ---------- */}
      <header className="relative overflow-hidden bg-gradient-to-br from-neutral-950 via-neutral-900 to-black text-white">
        <div className="absolute inset-0 opacity-[0.18] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,221,51,0.5) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-yellow-400/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-20 sm:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/15 border border-yellow-300/25 backdrop-blur text-xs font-medium uppercase tracking-widest text-yellow-200 mb-6">
                <Sparkles className="w-3.5 h-3.5" /> Customer Story
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-5">
                <span className="bg-gradient-to-r from-yellow-300 to-amber-400 bg-clip-text text-transparent">Banana Boat Rentals</span> runs Austin's largest party-boat fleet on BookingFlows.
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-xl mb-8">
                15+ boats. 500+ five-star reviews. One platform. Austin's largest
                party-boat fleet powers every booking, captain dispatch, and Stripe
                payout — across Lake Austin and Lake Travis — through BookingFlows.
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <a href="#contact"
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-yellow-400 text-black font-semibold hover:bg-yellow-300 transition-colors"
                   data-testid="bbr-hero-cta">
                  Run your fleet like Banana Boat <ArrowRight className="w-4 h-4" />
                </a>
                <a href={BBR_SITE} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium transition-colors backdrop-blur"
                   data-testid="bbr-hero-visit">
                  Visit bananaboatrentals.com <Globe className="w-4 h-4" />
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" /> 5.0 Google
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> Austin, TX
                </div>
                <div className="flex items-center gap-1.5">
                  <Anchor className="w-4 h-4" /> 15+ party boats
                </div>
              </div>
            </div>

            {/* Right side — clean collage of 3 sharp BBR photos with floating badges */}
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 grid grid-cols-2 grid-rows-3 gap-2 bg-white/5 p-2">
                <img src={proxyImg(HERO_PHOTOS[0], 900, 80)} alt="Banana Boat Rentals guests on Lake Austin with the Pennybacker Bridge"
                     className="col-span-2 row-span-2 w-full h-full object-cover rounded-2xl" loading="eager" decoding="async" />
                <img src={proxyImg(HERO_PHOTOS[1], 500, 80)} alt="Large group celebrating on a Banana Boat Rentals party boat"
                     className="w-full h-full object-cover rounded-2xl" loading="lazy" decoding="async" />
                <img src={proxyImg(HERO_PHOTOS[2], 500, 80)} alt="A Banana Boat Rentals pontoon cruising Lake Austin"
                     className="w-full h-full object-cover rounded-2xl" loading="lazy" decoding="async" />
              </div>
              {/* Floating logo — top-left */}
              <div className="absolute -top-3 -left-2 sm:-left-4 z-10 bg-black rounded-xl ring-1 ring-white/15 px-2.5 py-1.5 shadow-2xl">
                <img src={BBR_LOGO} alt="Banana Boat Rentals" className="h-8 sm:h-10 w-auto object-contain" loading="eager" />
              </div>
              {/* Floating fleet pill — top-right */}
              <div className="absolute -top-3 -right-2 sm:-right-4 z-10 bg-yellow-400 text-black rounded-xl px-3.5 py-2 font-bold text-sm shadow-2xl flex items-center gap-1.5">
                <Anchor className="w-4 h-4" /> 15+ boats
              </div>
              {/* Floating review pill — bottom-left */}
              <div className="absolute -bottom-5 -left-3 sm:-left-6 bg-white text-gray-900 rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3 max-w-[290px]">
                <GoogleGIcon className="w-7 h-7 flex-shrink-0" />
                <div className="text-xs leading-tight">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold">500+ Google reviews</span>
                    <div className="flex">
                      {[0,1,2,3,4].map(i => <Star key={i} className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />)}
                    </div>
                  </div>
                  <div className="text-gray-500 mt-0.5">Austin's largest party-boat fleet</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ---------- Big stats row ---------- */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="bbr-headline-stats">
            {HEADLINE_STATS.map(({ label, value, sub, icon: Icon, color }) => (
              <div key={label} className="relative bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.15)] overflow-hidden">
                <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full bg-gradient-to-br ${color} opacity-10`} />
                {Icon === "google"
                  ? <GoogleGIcon className="w-5 h-5 mb-3" />
                  : <Icon className="w-5 h-5 text-gray-400 mb-3" />}
                <div className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">{value}</div>
                <div className="mt-1 text-sm font-medium text-gray-700">{label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- The challenge ---------- */}
      <section className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600 mb-3">The Challenge</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
            The biggest fleet in town needs the tightest operation.
          </h2>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>
              Banana Boat Rentals didn't get to be Austin's largest party-boat fleet
              by accident — but running 15+ boats across Lake Austin and Lake Travis
              on weekends is a logistics machine. Availability, captains, deposits,
              and last-minute group changes all have to line up, every single Saturday.
            </p>
            <p>
              They needed two things at once: a website that actually ranks and
              converts, and a booking engine strong enough to dispatch a whole fleet
              without the phone-tag and spreadsheet chaos that breaks most operators
              the moment they scale past a couple of boats.
            </p>
          </div>
        </div>
      </section>

      {/* ---------- The solution — feature grid ---------- */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600 mb-3">The Solution</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              A world-class site, and the engine to run the fleet behind it.
            </h2>
            <p className="text-lg text-gray-600">
              We built Banana Boat Rentals a clean, modern, conversion-focused
              website — and wired it to BookingFlows so all 15+ boats book, dispatch,
              and pay out from one place.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="bbr-features">
            {FEATURES_USED.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-10 h-10 rounded-xl bg-warning-soft text-warning-soft-foreground flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Product in action — their real site ---------- */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600 mb-3">Product in Action</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Live on bananaboatrentals.com. Right now.
            </h2>
            <p className="text-lg text-gray-600">
              Not a mock-up — this is the real, modern site we designed and built for
              Banana Boat Rentals, with the BookingFlows booking engine running on it.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center" data-testid="bbr-product-screenshot">
            <div className="lg:col-span-7 relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-yellow-100 via-amber-50 to-orange-50 rounded-3xl blur-2xl opacity-70 pointer-events-none" />
              <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)] ring-1 ring-gray-200 bg-white">
                <img src={proxyImg(SITE_SHOTS[0].src, 1100, 82)} alt="The modern Banana Boat Rentals website built by BookingFlows"
                     className="w-full h-auto block" loading="lazy" decoding="async" />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-soft text-success-soft-foreground border border-emerald-200 text-[11px] font-medium uppercase tracking-wider mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live · bananaboatrentals.com
              </div>
              <h3 className="text-2xl font-semibold tracking-tight text-gray-900 mb-3">A clean, modern site engineered to convert.</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-5">
                Bold, fast, and mobile-first — the homepage leads with social proof
                (500+ Google reviews), the 15+ boat fleet, and a one-tap "Book Now"
                that drops straight into the BookingFlows widget. Customers never
                leave the brand, and availability is always live.
              </p>
              <a href={BBR_SITE} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors" data-testid="bbr-product-visit">
                See it live <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Two more real screenshots from the site we built */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-8 lg:mt-12" data-testid="bbr-product-gallery">
            {SITE_SHOTS.slice(1).map(({ src, title, caption }) => (
              <figure key={src} className="group">
                <div className="relative rounded-2xl overflow-hidden shadow-[0_24px_50px_-24px_rgba(15,23,42,0.35)] ring-1 ring-gray-200 bg-white">
                  <img src={proxyImg(src, 800, 82)} alt={title}
                       className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" decoding="async" />
                </div>
                <figcaption className="mt-4">
                  <div className="text-sm font-semibold text-gray-900">{title}</div>
                  <p className="text-sm text-gray-600 leading-relaxed mt-1">{caption}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Pull quote ---------- */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="relative bg-gradient-to-br from-neutral-950 to-neutral-800 rounded-3xl p-8 sm:p-14 text-white overflow-hidden">
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-yellow-400/15 blur-3xl" />
            <Quote className="absolute top-6 right-6 w-24 h-24 text-yellow-300/15" strokeWidth={1} />
            <blockquote className="relative">
              <p className="text-2xl sm:text-3xl font-medium leading-snug mb-6">
                "We have the largest fleet in Austin — and for the first time it all
                runs from one place. Customers book in seconds, captains know their
                boats, and our site finally looks as good as the experience on the water."
              </p>
              <footer className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-yellow-400 text-black flex items-center justify-center font-bold text-lg">B</div>
                <div>
                  <div className="font-semibold">Banana Boat Rentals Team</div>
                  <div className="text-sm text-gray-300">Austin, TX · bananaboatrentals.com</div>
                </div>
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ---------- Results ---------- */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-10">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-600 mb-3">The Results</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Austin's largest fleet, running like clockwork.
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-10" data-testid="bbr-results">
            {RESULTS.map(({ label, value, sub }) => (
              <div key={label} className="bg-white rounded-2xl p-5 border border-gray-100">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{label}</div>
                <div className="text-2xl font-bold text-gray-900">{value}</div>
                <div className="text-xs text-gray-500 mt-1.5">{sub}</div>
              </div>
            ))}
          </div>

          {/* Lifestyle photo gallery */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {HERO_PHOTOS.map((src, i) => (
              <div key={src} className={`relative rounded-2xl overflow-hidden shadow-md ${i === 0 ? "lg:col-span-2 aspect-[16/10]" : "aspect-[4/3]"}`}>
                <img src={proxyImg(src, i === 0 ? 900 : 600, 80)} alt={`Banana Boat Rentals day on the water ${i + 1}`}
                     className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Serving strip ---------- */}
      <section className="py-14 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 mb-6">
            Serving Austin's lakes
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-gray-400">
            {SERVING.map((p) => (
              <span key={p} className="text-sm sm:text-base font-medium tracking-wide hover:text-gray-700 transition-colors">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Closing CTA ---------- */}
      <section id="contact" className="py-20 sm:py-24 bg-gradient-to-br from-neutral-950 via-neutral-900 to-black text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/15 border border-yellow-300/25 backdrop-blur text-xs font-medium uppercase tracking-widest text-yellow-200 mb-6">
            <Zap className="w-3.5 h-3.5" /> Ready to scale
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-5">
            Run your fleet like Banana Boat Rentals.
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-9 leading-relaxed">
            Whether you run 2 boats or 20 — BookingFlows gives you the modern site,
            the local SEO, and the booking engine to grow into Austin's next biggest fleet.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            <a href="mailto:hello@bookingflows.com?subject=Demo%20request%20from%20Banana%20Boat%20Rentals%20case%20study"
               className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-yellow-400 text-black font-semibold hover:bg-yellow-300 transition-colors"
               data-testid="bbr-footer-cta-demo">
              Book a demo <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/marketplace"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white font-medium transition-colors backdrop-blur"
                  data-testid="bbr-footer-cta-explore">
              Explore the marketplace
            </Link>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 max-w-xl mx-auto text-sm">
            <div className="flex items-center gap-2 justify-center text-gray-400">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Stripe Connect ready
            </div>
            <div className="flex items-center gap-2 justify-center text-gray-400">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Fleet-scale dispatch
            </div>
            <div className="flex items-center gap-2 justify-center text-gray-400">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Custom site + SEO
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Minimal footer ---------- */}
      <footer className="py-10 px-4 bg-gray-950 text-gray-400">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/logo-icon.png" alt="BookingFlows" className="w-7 h-7 rounded-lg object-cover" />
            <span className="text-gray-300 group-hover:text-white transition-colors font-medium">BookingFlows</span>
            <span className="text-gray-500">· © {new Date().getFullYear()}</span>
          </Link>
          <div className="flex items-center gap-5">
            <a href={BBR_SITE} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" /> bananaboatrentals.com
            </a>
            <a href="tel:+17373085692" className="hover:text-white transition-colors flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> (737) 308-5692
            </a>
            <a href="mailto:hello@bookingflows.com" className="hover:text-white transition-colors flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> hello@bookingflows.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
